/**
 * Format: Product / Attraqt
 * ------------------------------------------------------------------------------
 * - Format a product response from the Attraqt API
 * - Outputted object should match that of the formatted Shopify API helper.
 */
import { v4 as uuidv4 } from 'uuid'
import { getFormattedCloudinaryImages, updateProductImageCDN } from '~/format/utils'
import { getProductTagValueByKey, isProductRestricted, isComingSoonProduct } from '~/helpers/product'
import { stringToHandle, stringToTitleCase } from '~/helpers/strings'
import { parseJSONUntilObject } from '~/helpers/utils'

import {
  getFormattedBadges,
  getFormattedLabels,
  getFormattedStyles,
  getFormattedTrendingBanners,
  getFormattedTypes,
  getFormattedWishlist,
  getFormattedWebExclusive,
  getInternationalSizing,
} from '~/format/utils'

/**
 * Formats response from an Attraqt product.
 * @param {Object} response - Attraqt product response.
 * @returns {Object}
 */
export function formatAttraqtProduct(response) {
  if (!response || !Object.keys(response).length) {
    throw new Error('[error-format-attraqt-product] Product object is missing entirely or empty.')
  }

  const handle = getAttraqtAttributeByName(response, 'handle') || 'invalid-handle'

  if (handle == 'invalid-handle') {
    throw new Error('[error-format-attraqt-product] Core product data missing.')
  }

  const { options, usesInternationalSizing } = getFormattedOptions(response)

  const tags = getAttraqtAttributeByName(response, 'shopify_tags')

  const url = mint.routes.root === '/'
    ? `//${location.host}/products/${handle}`
    : `//${location.host}${mint.routes.root}/products/${handle}`

  const formattedProduct = {
    attributes: {
      attraqt: getAttraqtProperties(response),
    },
    badges: getFormattedBadges(tags),
    comingSoon: isComingSoonProduct(tags),
    compareAtPrice: getFormattedPrice(response, 'v_presentment_compare_price'),
    handle,
    id: Number(response.id),
    images: getFormattedImages(response),
    inventory: Number(getAttraqtAttributeByName(response, 'v_inventory_quantity')),
    key: uuidv4(),
    label: getFormattedLabels(tags),
    linkedStyles: getFormattedLinkedStyles(response, tags, handle),
    options,
    source: 'attraqt',
    styleId: stringToHandle(getProductTagValueByKey(tags, 'style_id')),
    styles: getFormattedStyles(tags),
    price: getFormattedPrice(response, 'v_presentment_price'),
    tags,
    title: getAttraqtAttributeByName(response, 'product_title'),
    types: getFormattedTypes(tags),
    url,
    usesInternationalSizing,
    webExclusive: getFormattedWebExclusive(tags),
    wishlist: getFormattedWishlist(tags),
  }

  formattedProduct.available = !(formattedProduct.inventory < 1)
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.giftCard = formattedProduct.types.shopify === 'Gift Cards'
  formattedProduct.variants = getFormattedVariants(response)
  formattedProduct.onlyHasDefaultVariant = Boolean(formattedProduct.variants.length === 1)

  formattedProduct.restricted = isProductRestricted(formattedProduct),
  formattedProduct.trendingBanner = getFormattedTrendingBanners(formattedProduct)

  return formattedProduct
}

/**
 * Return a formatted array of linked styles.
 * @param {Object} product - Unformatted GraphQL product.
 * @param {Array} tags - Array of product tags.
 * @param {String} handle - Shopify product handle.
 * @returns {Array}
 */
function getFormattedLinkedStyles(response, tags, handle) {

  /**
   * Colours of the current product.
   */
  const currentStyle = {
    current: true,
    handle,
    colour: {
      group: stringToTitleCase(getProductTagValueByKey(tags, 'colour_group')),
      handle: stringToHandle(getProductTagValueByKey(tags, 'colour')),
      name: stringToTitleCase(getProductTagValueByKey(tags, 'colour')),
      swatch: '',
    },
    styleId: stringToHandle(getProductTagValueByKey(tags, 'style_id')),
  }

  /**
   * Linked styles.
   * - Note: Out of Stock product(s) may not be synced to Attraqt
   *   and may not appear in this array.
   */
  let colourData = getAttraqtAttributeByName(response, 'colour_swatch') || '{}'
  colourData = JSON.parse(colourData)

  const linkedStyleReference = getProductTagValueByKey(tags, 'LinkedStyleReference')

  /**
   * Return early if:
   * - The colourData object has no values
   * - There is no value in the object matching the linkedStyleReference
   * - The matching value is falsy
   */
  if (
    !Object.keys(colourData).length ||
    !colourData[linkedStyleReference] ||
    !colourData[linkedStyleReference]?.colours
  ) {
    return [currentStyle]
  }

  /**
   * Filter any falsy values from the `colours` array.
   */
  const colours = colourData[linkedStyleReference].colours.filter(Boolean)

  if (colours.length === 0) {
    console.warn(
      `[error-invalid-swatches] Attraqt product data contained a colour_swatch attribute with invalid or empty data. No swatches will be displayed for the product with handle '${handle}'.`,
    )
  }

  const linkedStyles = colours.map((colour) => {
    return {
      current: false,
      colour: {
        group: colour?.t_colour_group,
        handle: colour?.handle,
        name: colour?.t_colour,
        swatch: colour?.swatch_image,
      },
      styleId: '',
    }
  })

  return [
    currentStyle,
    ...linkedStyles,
  ]
}

/**
 * Return images.
 * @param {Object} response - Attraqt product response.
 * @returns {Boolean|Array}
 */
function getFormattedImages(response) {
  const useCloudinaryCDN = mint.flags.useCloudinaryCDN
  const productTitle = getAttraqtAttributeByName(response, 'product_title')

  let images

  if (useCloudinaryCDN) {
    let imageAttribute = getAttraqtAttributeByName(response, 'cloudinary_productmedia_v1')

    imageAttribute = JSON.parse(JSON.parse(imageAttribute))
    images = getFormattedCloudinaryImages(imageAttribute, productTitle)
  }

  if (!useCloudinaryCDN || !images) {
    images = getFormattedShopifyImages(response, productTitle)
  }

  if (!images) {
    return false
  }

  return images
}

/**
 * Return formatted images hosted by Shopify.
 * @param {Object} response - Attraqt product response.
 * @param {String} productTitle - Product Title
 * @returns {Boolean|Array}
 */
function getFormattedShopifyImages(response, productTitle) {
  const imageAttribute = getAttraqtAttributeByName(response, 'images')

  if (!imageAttribute) return false

  const imageUrls = imageAttribute.split(';;')

  /**
   * Attraqt will return a hard image URL
   * - This URL may be the older caching URL for images
   * - This is the reason why URL is updated before reattaching
   * - Takes advantage of CDN and always serve latest image.
   */
  const images = imageUrls.map((url, index) => {
    const updatedSrc = updateProductImageCDN(url)

    return {
      publicId: '',
      source: 'shopify',
      alt: `${productTitle} (Image ${index}).`,
      src: updatedSrc,
    }
  })

  return images
}

/**
 * Return options and variable indicating if international sizes are enabled.
 * @param {Object} response - Attraqt product response.
 * @returns {Object}
 */
function getFormattedOptions(response) {
  let usesInternationalSizing = false

  const optionAttributes = [
    'v_length',
    'v_size',
    'variant_v_length',
    'variant_v_size',
  ]

  const optionTypes = optionAttributes.map((attribute, index) => {
    const attrValue = getAttraqtAttributeByName(response, attribute)

    if (!attrValue) {
      return false
    }

    const name = attribute
      .replace(/v_/g, '')
      .replace(/variant_/g, '')

    const values = Array.isArray(attrValue)
      ? attrValue
      : attrValue.split(';;')

    return {
      index,
      name: stringToTitleCase(name),
      values,
    }
  }).filter(Boolean)

  /**
   * International sizing check for each of the product's options.
   * - Only runs if an option's name contains `size`.
   */
  const internationalSizes = getProductIntlSizes(response)

  if (internationalSizes) {
    for (const option of optionTypes) {
      const isSizingOption = stringToHandle(option.name) === 'size'

      if (!isSizingOption) {
        continue
      }

      const intlValues = option.values.map(optionValue => {
        const match = getInternationalSizing(optionValue, internationalSizes)

        if (!match) {
          return false
        }

        return match
      }).filter(Boolean)

      if (intlValues.length === option.values.length) {
        option.values = intlValues
        usesInternationalSizing = true
      }
    }
  }

  return { options: optionTypes, usesInternationalSizing }
}

/**
 * Return a formatted array of currency prices (GBP, EUR, USD etc.)
 * @param {Object} response - Attraqt product response.
 * @param {String} priceType - Price or Compare At -- uses the prefix of
 * the Attribute name in Attraqt. Example: Attribute name is `compare_at_price_gbp`,
 * so pass in `compare_at_price_`
 * @returns {Array}
 */

/**
 * Returns a formatted price object based off of the current currency.
 * - Attraqt stores several currency prices in different fields, e.g.
 *  `price_$code`, e.g. `price_gbp`, `price_usd`
 * - Return a single correct item.
 * @param {Object} response - Attraqt product response.
 * @param {String} priceType - Attraqt price attribute name.
 * @returns {Object}
 */
function getFormattedPrice(response, priceType = 'v_presentment_price') {
  const formattedCurrency = mint.shop.currency.toLowerCase()

  const attributeName = `${priceType}_${formattedCurrency}`
  const attributeValue = getAttraqtAttributeByName(response, attributeName)

  return {
    amount: (Number(attributeValue) * 100) ?? false,
    currencyCode: mint.shop.currency,
  }
}

/**
 * Parse and return Attraqt query information.
 * @param {Object} response - Attraqt product response.
 * @returns {Object}
 */
function getAttraqtProperties(response) {
  const link = response.link.find((link) => link.type === 'detail')

  const params = link['url-params']
    ? Object.fromEntries(new URLSearchParams(link['url-params']))
    : false

  return {
    id: response.id,
    params,
  }
}

/**
 * Return a specific product attribute from an Attraqt product by name
 * @param {Object} response - Attraqt product response.
 * @param {String} name - Attraqt defined product attribute (handle, v_price etc.)
 * @returns {Boolean|Any}
 */
function getAttraqtAttributeByName(response, name) {
  if (!response || !name) {
    return false
  }

  const attribute = response.attribute.find((attribute) => {
    return attribute.name === name
  })

  if (!attribute) {
    return false
  }

  let attributeValues = attribute.value.map((val) => {
    return val.value
  })

  if (!attributeValues) {
    return false
  }

  if (attributeValues.length === 1) {
    attributeValues = attributeValues[0]
  }

  return attributeValues
}

/**
 * Parse and return an array of variants.
 * - Attraqt doesn't store any variant data, except for the ID
 * - Return an object with *just* the ID to match Shopify formatting.
 * @param {Object} response - Attraqt product response.
 * @returns {Object|Boolean}
 */
function getFormattedVariants(response) {
  let variants = getAttraqtAttributeByName(response, 'secondid')

  /**
   * Only one variant returned (Product has no variants).
   */
  if (typeof variants === 'string') {
    variants = [variants]
  }

  if (!variants) {
    return []
  }

  const variantIds = variants.map((variant) => {
    if (!variant.startsWith('v_')) {
      return false
    }

    const id = Number(variant.replace(/v_/g, ''))

    return {
      id,
    }
  }).filter(Boolean)

  return variantIds
}

/**
 * Return a parsed JSON object of the product's international sizing metafield.
 * - If the metafield does not exist, return false.
 * - If the feature flag for localized sizes is disabled, return false.
 * @param {Object} response - Response object.
 * @returns {Boolean|Object}
 */
function getProductIntlSizes(response) {
  const featureFlag = mint.flags?.international?.showLocalisedSizes
  const sizes = getAttraqtAttributeByName(response, 'global_mv_international_size')

  if (!featureFlag || !sizes) {
    return false
  }

  return parseJSONUntilObject(sizes)
}
